import { api } from '../../lib/api';
import type {
  BookingQuoteRequest,
  BookingQuoteResponse,
  ConfirmBookingRequest,
  BookingConfirmationDTO,
  BookingLookupResponse,
  ModifyRequoteResponse,
  OtpRequestResponse,
} from '@pms/shared';

/**
 * OTA-standard confirm body. Extends the shared contract with party composition
 * (adults/children — `guests` stays authoritative as adults+children), optional
 * check-in details, and the required emailed OTP. Sent as-is to the backend,
 * which owns the canonical shared type; kept local until that lands.
 */
export interface ConfirmBookingBody extends ConfirmBookingRequest {
  adults: number;
  children: number;
  guestNames?: string;
  arrivalTime?: string;
  otp: string;
}

export async function getQuote(body: BookingQuoteRequest): Promise<BookingQuoteResponse> {
  const { data } = await api.post<BookingQuoteResponse>('/bookings/quote', body);
  return data;
}

/** Confirm a booking. The Idempotency-Key is owned by the caller (one per attempt). */
export async function confirmBooking(
  body: ConfirmBookingBody,
  idempotencyKey: string,
): Promise<BookingConfirmationDTO> {
  const { data } = await api.post<BookingConfirmationDTO>('/bookings/confirm', body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return data;
}

/**
 * Request a 6-digit booking-verification code, emailed to the guest.
 * Always resolves 200 (enumeration-safe); `devCode` present only off-prod.
 * Same public endpoint the guest-login / lead flows use.
 */
export async function requestBookingOtp(email: string): Promise<OtpRequestResponse> {
  const { data } = await api.post<OtpRequestResponse>('/auth/otp/request', { email });
  return data;
}

/** Cold-load lookup by reference + PIN (no login). */
export async function getBooking(ref: string, pin: string): Promise<BookingLookupResponse> {
  const { data } = await api.get<BookingLookupResponse>(`/bookings/${ref}`, { params: { pin } });
  return data;
}

/** Re-send the confirmation email (ref + PIN). */
export async function resendConfirmation(ref: string, pin: string): Promise<void> {
  await api.post(`/bookings/${ref}/resend`, null, { params: { pin } });
}

/** Cancel a booking (ref + PIN). Returns the updated (cancelled) booking. */
export async function cancelBooking(ref: string, pin: string, reason?: string): Promise<BookingLookupResponse> {
  const { data } = await api.post<BookingLookupResponse>(`/bookings/${ref}/cancel`, { pin, ...(reason ? { reason } : {}) });
  return data;
}

/** Read-only price + availability preview for new dates. */
export async function requoteBooking(ref: string, pin: string, checkIn: string, checkOut: string): Promise<ModifyRequoteResponse> {
  const { data } = await api.get<ModifyRequoteResponse>(`/bookings/${ref}/requote`, { params: { pin, checkIn, checkOut } });
  return data;
}

/** Apply a date change (ref + PIN; server re-prices). */
export async function modifyBooking(
  ref: string,
  body: { pin: string; checkIn: string; checkOut: string; quotedTotal: number },
): Promise<BookingLookupResponse> {
  const { data } = await api.patch<BookingLookupResponse>(`/bookings/${ref}`, body);
  return data;
}
